import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Package, CreditCard, User as UserIcon } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SIZES = ["S", "M", "L", "XL", "2XL", "3XL"];
const FITS = ["Unisex", "Men's", "Women's"];

const Account = () => {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [orders, setOrders] = useState([]);

  const loadSub = useCallback(() => api.get("/subscriptions/mine").then((r) => setSub(r.data)), []);
  const loadOrders = useCallback(() => api.get("/orders/mine").then((r) => setOrders(r.data)), []);

  useEffect(() => {
    loadSub();
    loadOrders();
  }, [loadSub, loadOrders]);

  const updateSub = async (updates) => {
    try {
      const res = await api.put(`/subscriptions/${sub.id}`, updates);
      setSub(res.data);
      toast.success("Subscription updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const cancelSub = async () => {
    await updateSub({ status: "cancelled" });
    setSub(null);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12" data-testid="account-page">
      <div className="flex items-center gap-3 mb-2">
        <UserIcon className="w-8 h-8 text-[#D35400]" strokeWidth={2.2} />
        <h1 className="font-display text-6xl">My Account</h1>
      </div>
      <p className="text-[#4A4A4A] mb-10">Howdy, {user?.name} · {user?.email}</p>

      {/* SUBSCRIPTION */}
      <section className="mb-12">
        <h2 className="font-display text-4xl mb-4 flex items-center gap-2"><CreditCard className="w-6 h-6 text-[#D35400]" /> Tee Club Subscription</h2>
        {sub ? (
          <div className="border-2 border-[#1C1C1C] hard-shadow bg-[#F5F2EA] p-6" data-testid="subscription-status">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <span className={`inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 ${sub.status === "active" ? "bg-[#2C3E50] text-white" : "bg-[#C0392B] text-white"}`} data-testid="sub-status-badge">
                  {sub.status}
                </span>
                <p className="mt-3 text-lg"><strong>${sub.price.toFixed(2)}</strong> / month · {sub.fit} · Size {sub.size}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div>
                <label className="font-display text-xl">Fit</label>
                <Select value={sub.fit} onValueChange={(v) => updateSub({ fit: v })}>
                  <SelectTrigger className="border-2 border-[#1C1C1C] rounded-none mt-1 bg-white" data-testid="sub-fit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FITS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-display text-xl">Size</label>
                <Select value={sub.size} onValueChange={(v) => updateSub({ size: v })}>
                  <SelectTrigger className="border-2 border-[#1C1C1C] rounded-none mt-1 bg-white" data-testid="sub-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              {sub.status === "active" ? (
                <button onClick={() => updateSub({ status: "paused" })} className="btn-ghost px-6 py-3" data-testid="sub-pause">Pause</button>
              ) : (
                <button onClick={() => updateSub({ status: "active" })} className="btn-primary px-6 py-3" data-testid="sub-resume">Resume</button>
              )}
              <button onClick={cancelSub} className="px-6 py-3 border-2 border-[#C0392B] text-[#C0392B] font-bold uppercase tracking-widest hover:bg-[#C0392B] hover:text-white transition" data-testid="sub-cancel">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-[#4A4A4A] p-8 text-center" data-testid="no-subscription">
            <p className="text-[#4A4A4A] mb-4">You're not in the Tee Club yet.</p>
            <Link to="/tee-club" className="btn-primary px-8 py-4 inline-block" data-testid="account-join-club">Join the Tee Club</Link>
          </div>
        )}
      </section>

      {/* ORDERS */}
      <section>
        <h2 className="font-display text-4xl mb-4 flex items-center gap-2"><Package className="w-6 h-6 text-[#D35400]" /> Order History</h2>
        {orders.length === 0 ? (
          <div className="border-2 border-dashed border-[#4A4A4A] p-8 text-center" data-testid="no-orders">
            <p className="text-[#4A4A4A]">No orders yet. <Link to="/shop" className="underline font-bold text-[#D35400]">Start shopping</Link>.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="border-2 border-[#1C1C1C] bg-[#F5F2EA] p-5" data-testid={`order-${o.id}`}>
                <div className="flex flex-wrap justify-between gap-3 mb-3">
                  <div>
                    <span className="text-xs uppercase tracking-widest text-[#4A4A4A]">Order #{o.id.slice(0, 8)}</span>
                    <p className="font-bold text-lg text-[#C0392B]">${o.total.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase px-2 py-1 bg-[#2C3E50] text-white">{o.status}</span>
                    <span className={`text-xs font-bold uppercase px-2 py-1 ${o.fulfilled ? "bg-[#27ae60] text-white" : "bg-[#EDE8DD] text-[#4A4A4A]"}`}>
                      {o.fulfilled ? "Fulfilled" : "Processing"}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-[#4A4A4A]">
                  {o.items.map((i, idx) => (
                    <span key={`${i.product_id}-${i.size}-${idx}`}>{i.name} ({i.size}) ×{i.quantity}{idx < o.items.length - 1 ? " · " : ""}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Account;
